import React, { useState, useEffect } from 'react';
import { Search, Plus, Phone, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiClient } from '../../utils/apiHelper';

const PhoneLog = () => {
  const [phoneLogs, setPhoneLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    call_date: new Date().toISOString().split('T')[0],
    call_time: new Date().toTimeString().slice(0, 5),
    caller_name: '',
    phone_number: '',
    call_type: 'Incoming',
    purpose: '',
    duration: '',
    received_by: '',
    notes: '',
    status: 'Completed'
  });

  useEffect(() => { fetchPhoneLogs(); }, []);

  const fetchPhoneLogs = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/frontdesk/phone-logs');
      setPhoneLogs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching phone logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/frontdesk/phone-logs', formData);
      alert('Phone log added successfully!');
      setShowModal(false);
      resetForm();
      fetchPhoneLogs();
    } catch (error) {
      alert('Failed to add phone log');
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this phone log?')) return;
    try {
      await apiClient.delete(`/api/frontdesk/phone-logs/${logId}`);
      fetchPhoneLogs();
    } catch (error) {
      alert('Failed to delete phone log');
    }
  };

  const resetForm = () => {
    setFormData({
      call_date: new Date().toISOString().split('T')[0],
      call_time: new Date().toTimeString().slice(0, 5),
      caller_name: '',
      phone_number: '',
      call_type: 'Incoming',
      purpose: '',
      duration: '',
      received_by: '',
      notes: '',
      status: 'Completed'
    });
  };

  const filteredLogs = phoneLogs.filter(log =>
    log.caller_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.phone_number?.includes(searchTerm) ||
    log.purpose?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Phone Log</CardTitle>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" /> Add Phone Log
          </Button>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search by name, phone, or purpose..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Caller Name</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Phone Number</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Call Type</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Purpose</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-8 text-gray-500">
                      {loading ? 'Loading...' : 'No phone logs found. Click "Add Phone Log" to create a new entry.'}
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="p-3 text-sm">{log.call_date} {log.call_time}</td>
                      <td className="p-3 text-sm font-medium">{log.caller_name}</td>
                      <td className="p-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          {log.phone_number}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          log.call_type === 'Incoming' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {log.call_type}
                        </span>
                      </td>
                      <td className="p-3 text-sm">{log.purpose}</td>
                      <td className="p-3 text-sm">{log.duration || '-'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          log.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          log.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <button onClick={() => handleDelete(log.id)} className="text-red-600 hover:text-red-800">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Phone Log Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add Phone Log</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" required value={formData.call_date} onChange={(e) => setFormData({...formData, call_date: e.target.value})} />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input type="time" required value={formData.call_time} onChange={(e) => setFormData({...formData, call_time: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Caller Name *</Label>
                  <Input required value={formData.caller_name} onChange={(e) => setFormData({...formData, caller_name: e.target.value})} />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input required value={formData.phone_number} onChange={(e) => setFormData({...formData, phone_number: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Call Type *</Label>
                  <Select value={formData.call_type} onValueChange={(v) => setFormData({...formData, call_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Incoming">Incoming</SelectItem>
                      <SelectItem value="Outgoing">Outgoing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Duration</Label>
                  <Input placeholder="e.g., 5 mins" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Purpose *</Label>
                <Input required value={formData.purpose} onChange={(e) => setFormData({...formData, purpose: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Received By</Label>
                  <Input value={formData.received_by} onChange={(e) => setFormData({...formData, received_by: e.target.value})} />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Missed">Missed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit">Add Log</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneLog;
